/**
 * Badge geometry — decided, still prototype-local.
 *
 * DECIDED: Pill. 22px tall, fully round, 12px sans, sentence case, and always
 * a tonal fill.
 *
 * THE RULE, which is the part that has to survive contact with the button: a
 * badge always carries a tonal wash and never a ring; a low-weight button
 * carries a ring or nothing and never a tonal wash. A solid badge is therefore
 * forbidden — filled with brand or ink it is a button, and no amount of height
 * says otherwise. That is why `TONE` below is the only fill a badge is given,
 * and why there is no `TONE.brand`.
 *
 * Fully round over the 8px corner that would have matched the controls. The
 * original argument for it was exclusivity — a shape nothing else on the page
 * shared was the cheapest way to say "not pressable". That argument is gone:
 * the button went fully round too (`pill.ts`), and the badge keeps the pill
 * anyway.
 *
 * What separates them now is size and fill, which turn out to be the stronger
 * signals. 22px against 38px is a difference you read before you read the
 * corner, and a badge carries a `--nf-ds-tone-*` fill — a quiet wash with its
 * own foreground — where a button carries brand or ink, or no fill at all.
 * Tone is the part that does the real work: nothing in the system asks you to
 * press a pale amber block.
 *
 * The soft corner was reconsidered when the button moved and rejected again,
 * for the reason it lost the first time: a 22px soft-cornered chip beside a
 * 38px field reads as a button you cannot click. Shape no longer signals
 * pressability in this system, and forcing it to would cost more than it buys.
 *
 * Sans and sentence case, never mono uppercase. Mono uppercase is the `rail`
 * role — structure, like a table head — and 83 hand-rolled pills currently
 * confuse the two. A badge is content: one value from a small set.
 *
 * The tones come from the shipped `--nf-ds-tone-*` roles, which are already
 * decided and already carry their own foreground. Only the geometry was open.
 */

/** 22px, fully round, 12px sans. */
export const BADGE =
  "inline-flex h-nf-ds-badge items-center gap-nf-ds-tight rounded-nf-ds-pill px-nf-ds-3 font-nf-ds-sans text-[12px] font-[500] leading-none whitespace-nowrap";

/**
 * The status dot, for when the label is already in the row.
 *
 * Many hand-rolled "pills" are 1–2px tall — dots and bars rather than labels —
 * because the component never covered this and people made their own. Pair it
 * with plain text; it is not a badge with the words removed.
 */
export const BADGE_DOT = "size-2 shrink-0 rounded-full";

/** Background and foreground together — a tone is never one without the other. */
export const TONE = {
  ok: "bg-nf-ds-tone-ok-bg text-nf-ds-tone-ok-fg",
  warn: "bg-nf-ds-tone-warn-bg text-nf-ds-tone-warn-fg",
  err: "bg-nf-ds-tone-err-bg text-nf-ds-tone-err-fg",
  info: "bg-nf-ds-tone-info-bg text-nf-ds-tone-info-fg",
  idle: "bg-nf-ds-tone-neutral-bg text-nf-ds-tone-neutral-fg",
} as const;

export type Tone = keyof typeof TONE;

/**
 * The tone as a vivid status colour, for a dot that stands for an answer: a
 * Segmented status option not chosen, or the same answer in a select. The
 * pale fill below all but vanishes on a control's grey ground, and the dark
 * text colours read as brown at 8px, so warn would not look yellow.
 */
export const TONE_DOT: Record<Tone, string> = {
  ok: "bg-nf-ds-success",
  warn: "bg-nf-ds-warning",
  err: "bg-nf-ds-destructive",
  info: "bg-nf-ds-info",
  idle: "bg-nf-ds-tone-neutral-fg",
};

/** The fill alone, for a dot — which has no text of its own to colour. */
export const TONE_FILL: Record<Tone, string> = {
  ok: "bg-nf-ds-tone-ok-bg",
  warn: "bg-nf-ds-tone-warn-bg",
  err: "bg-nf-ds-tone-err-bg",
  info: "bg-nf-ds-tone-info-bg",
  idle: "bg-nf-ds-tone-neutral-bg",
};
